import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FilesApi } from '@up2stay/up2promo-api-client';
import { Button } from '@up2stay/up2promo-ui';
import { ConfirmDialog } from '@up2stay/up2promo-ui';
import { Input } from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import { FileText, GripVertical, Plus, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

import type { ModuleEntryGovernanceProps } from '@/core/components/profile/module-entry-governance';
import {
  useEntryGovernance,
  WholeModuleGovernanceHeader,
} from '@/core/components/profile/module-entry-governance-ui';
import { useDndProfileSensors } from '@/core/hooks/use-dnd-profile-sensors';
import { useIsMobile } from '@/core/hooks/use-mobile';
import {
  lockBodyScrollDuringDrag,
  triggerDragEndHaptic,
  triggerDragOverHaptic,
  triggerDragStartHaptic,
  unlockBodyScrollAfterDrag,
} from '@/core/lib/dnd-haptics';
import { MODULE_ENTRY_WHOLE_MODULE_KEY } from '@/core/lib/profile-module-entry-policies';
import { useDictionary } from '@/core/providers/dictionary-provider';
import type { FileItem, FilesModuleConfig } from '@/core/types/profile-modules';

const MAX_FILE_SIZE_MB = 20;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ACCEPT_FILE_TYPES =
  '.pdf,.doc,.docx,.xls,.xlsx,.txt,.odt,.ods,.csv,.jpg,.jpeg,.png,.gif,.webp';

function reorderItems<T>(items: T[], fromIndex: number, toIndex: number): T[] {
  const result = [...items];
  const removed = result.splice(fromIndex, 1)[0];
  if (removed === undefined) return items;
  result.splice(toIndex, 0, removed);
  return result;
}

interface SortableFileItemProps {
  item: FileItem;
  index: number;
  onTitleChange: (title: string) => void;
  onRemove: () => void;
  structureReadOnly?: boolean;
  contentDisabled?: boolean;
}

function SortableFileItem({
  item,
  index,
  onTitleChange,
  onRemove,
  structureReadOnly = false,
  contentDisabled = false,
}: SortableFileItemProps) {
  const isMobile = useIsMobile();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `file-${index}`, disabled: structureReadOnly });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? transition : 'none',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-3 p-3 border rounded-lg bg-card',
        isDragging && 'opacity-80 shadow-lg z-10 ring-2 ring-primary',
        isMobile && 'touch-pan-y cursor-grab active:cursor-grabbing',
      )}
      {...(isMobile && !structureReadOnly
        ? { ...listeners, ...attributes }
        : {})}
    >
      {!structureReadOnly ? (
        <div
          data-dnd-grip
          {...listeners}
          {...attributes}
          className={cn(
            'touch-none shrink-0 rounded p-0.5 cursor-grab active:cursor-grabbing',
          )}
          aria-label="Reihenfolge ändern"
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>
      ) : null}
      <FileText className="h-5 w-5 shrink-0 text-muted-foreground" />
      <Input
        value={item.title ?? ''}
        onChange={(e) => onTitleChange(e.target.value)}
        placeholder="Titel der Datei"
        className="flex-1 min-w-0 h-9"
        disabled={contentDisabled}
      />
      {!structureReadOnly ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label="Datei entfernen"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      ) : null}
    </div>
  );
}

interface FilesModuleConfigProps extends ModuleEntryGovernanceProps {
  config: FilesModuleConfig;
  onChange: (config: FilesModuleConfig) => void;
  fieldLockedHintMessage?: string;
}

export function FilesModuleConfigComponent({
  config,
  onChange,
  templateModule,
  showEntryPolicies = false,
  onEntryPolicyChange,
  policyLabels,
  fieldLockedHintMessage,
  structureReadOnly: _structureReadOnly = false,
  contentReadOnly = false,
}: FilesModuleConfigProps) {
  const dict = useDictionary();
  const filesDict = dict.profiles.edit.modules.files;
  const { entryLocked } = useEntryGovernance({
    templateModule,
    entryId: MODULE_ENTRY_WHOLE_MODULE_KEY,
  });
  const moduleContentLocked =
    contentReadOnly || (entryLocked && !showEntryPolicies);

  const [localConfig, setLocalConfig] = useState<FilesModuleConfig>(config);
  const [prevConfig, setPrevConfig] = useState(config);
  if (config !== prevConfig) {
    setPrevConfig(config);
    setLocalConfig(config);
  }
  const [isUploading, setIsUploading] = useState(false);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRestoreRef = useRef<string | null>(null);
  const lastOverIdRef = useRef<UniqueIdentifier | null>(null);
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(
    null,
  );

  const handleChange = (updates: Partial<FilesModuleConfig>) => {
    if (moduleContentLocked) return;
    const newConfig = { ...localConfig, ...updates };
    setLocalConfig(newConfig);
    onChange(newConfig);
  };

  const files = localConfig.files || [];

  const addFiles = (newItems: FileItem[]) => {
    const withOrder = newItems.map((item, i) => ({
      ...item,
      order: files.length + i,
    }));
    handleChange({ files: [...files, ...withOrder] });
  };

  const requestRemoveFile = (index: number) => {
    setPendingRemoveIndex(index);
  };

  const confirmRemoveFile = () => {
    if (pendingRemoveIndex == null) return;
    const index = pendingRemoveIndex;
    const reordered = files
      .filter((_, i) => i !== index)
      .map((f, i) => ({ ...f, order: i }));
    handleChange({ files: reordered });
    setPendingRemoveIndex(null);
  };

  const updateFileTitle = (index: number, title: string) => {
    const next = [...files];
    const current = next[index];
    if (!current) return;
    next[index] = { ...current, title: title || undefined };
    handleChange({ files: next });
  };

  const handleDragStart = () => {
    lastOverIdRef.current = null;
    scrollRestoreRef.current = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (over == null) {
      lastOverIdRef.current = null;
      return;
    }
    const isNewSlot = lastOverIdRef.current !== over.id;
    const isBackToOwnSlot =
      over.id === active.id && lastOverIdRef.current === null;
    if (isNewSlot && !isBackToOwnSlot) {
      void triggerDragOverHaptic();
    }
    lastOverIdRef.current = over.id;
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (scrollRestoreRef.current !== null) {
      document.body.style.overflow = scrollRestoreRef.current;
      scrollRestoreRef.current = null;
    }
    const { active, over } = event;
    if (over == null || active.id === over.id) return;
    const fromIndex = files.findIndex((_, i) => `file-${i}` === active.id);
    const toIndex = files.findIndex((_, i) => `file-${i}` === over.id);
    if (fromIndex === -1 || toIndex === -1) return;
    const reordered = reorderItems(files, fromIndex, toIndex).map((f, i) => ({
      ...f,
      order: i,
    }));
    handleChange({ files: reordered });
  };

  const uploadFilesAndAdd = async (fileList: FileList | File[]) => {
    if (moduleContentLocked) return;
    const list = Array.from(fileList);
    const valid: File[] = [];
    for (const f of list) {
      if (f.size > MAX_FILE_SIZE_BYTES) {
        toast.error(
          filesDict.fileTooLarge
            .replace('{name}', f.name)
            .replace('{maxMb}', String(MAX_FILE_SIZE_MB)),
        );
        continue;
      }
      const ext = f.name.split('.').pop()?.toLowerCase() || '';
      const allowed = new Set(
        'pdf,doc,docx,xls,xlsx,txt,odt,ods,csv,jpg,jpeg,png,gif,webp'.split(
          ',',
        ),
      );
      if (!allowed.has(ext)) {
        toast.error(filesDict.fileTypeNotAllowed.replace('{ext}', ext));
        continue;
      }
      valid.push(f);
    }
    if (!valid.length) return;

    setIsUploading(true);
    const newItems: FileItem[] = [];

    try {
      for (let i = 0; i < valid.length; i++) {
        const file = valid[i];
        if (!file) continue;
        const res = await FilesApi.uploadFile({ body: { file } });
        if (res.data?.file) {
          // Signed GET URL is for immediate UI; persist strips it (DEV-458).
          newItems.push({
            id: crypto.randomUUID(),
            file: { id: res.data.file.id, path: res.data.file.path },
            title: file.name.replace(/\.[^/.]+$/, '') || file.name,
            order: files.length + newItems.length,
          });
        }
      }
      if (newItems.length > 0) {
        addFiles(newItems);
        toast.success(
          newItems.length === 1
            ? filesDict.addedOne
            : filesDict.addedMany.replace('{count}', String(newItems.length)),
        );
      }
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error(filesDict.uploadFailed);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files;
    if (!list?.length) return;
    await uploadFilesAndAdd(list);
    e.target.value = '';
  };

  const handleFileDragOver = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDraggingFile(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDraggingFile(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    setIsDraggingFile(false);
    if (!e.dataTransfer.files?.length) return;
    e.preventDefault();
    await uploadFilesAndAdd(e.dataTransfer.files);
  };

  const sensors = useDndProfileSensors();

  return (
    <div className="space-y-4">
      <WholeModuleGovernanceHeader
        templateModule={templateModule}
        showEntryPolicy={showEntryPolicies}
        onEntryPolicyChange={onEntryPolicyChange}
        policyLabels={policyLabels}
        fieldLockedHintMessage={fieldLockedHintMessage}
        enableMemberValues={false}
      />
      <div
        className={cn(
          'space-y-3 transition-colors',
          isDraggingFile && 'rounded-lg ring-2 ring-primary bg-muted/50',
          moduleContentLocked && 'pointer-events-none opacity-70',
        )}
        onDragOver={handleFileDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT_FILE_TYPES}
          multiple
          className="hidden"
          onChange={handleFileChange}
          disabled={isUploading || moduleContentLocked}
        />

        {files.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={() => {
              handleDragStart();
              lockBodyScrollDuringDrag();
              void triggerDragStartHaptic();
            }}
            onDragOver={handleDragOver}
            onDragEnd={(e) => {
              handleDragEnd(e);
              unlockBodyScrollAfterDrag();
              void triggerDragEndHaptic();
            }}
          >
            <SortableContext
              items={files.map((_, i) => `file-${i}`)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {files.map((file, index) => (
                  <SortableFileItem
                    key={file.id ?? file.file?.id ?? index}
                    item={file}
                    index={index}
                    onTitleChange={(title) => updateFileTitle(index, title)}
                    onRemove={() => requestRemoveFile(index)}
                    structureReadOnly={moduleContentLocked}
                    contentDisabled={moduleContentLocked}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : null}
        {!moduleContentLocked ? (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className={cn(
              'group flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-muted-foreground/30 px-4 py-5',
              'text-muted-foreground transition-colors hover:border-primary hover:bg-primary/5 hover:text-primary',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
            aria-label="Datei hinzufügen"
          >
            {isUploading ? (
              <span className="text-sm">Datei wird hochgeladen…</span>
            ) : (
              <>
                <Plus className="h-5 w-5" />
                <span className="font-medium">Datei hinzufügen</span>
              </>
            )}
          </button>
        ) : null}
        <p className="text-xs text-muted-foreground text-center">
          Dateien hierher ziehen oder über „Datei hinzufügen“ auswählen. Max. 20
          MB. Erlaubt: PDF, Office, Bilder, TXT, CSV.
        </p>
      </div>

      <ConfirmDialog
        open={pendingRemoveIndex !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRemoveIndex(null);
        }}
        title="Datei entfernen?"
        description={
          pendingRemoveIndex != null &&
          files[pendingRemoveIndex]?.title?.trim() ? (
            <>
              Möchtest du{' '}
              <span className="font-medium">
                {files[pendingRemoveIndex].title}
              </span>{' '}
              wirklich entfernen? Diese Aktion kann nicht rückgängig gemacht
              werden.
            </>
          ) : (
            'Möchtest du diese Datei wirklich entfernen? Diese Aktion kann nicht rückgängig gemacht werden.'
          )
        }
        confirmLabel="Entfernen"
        cancelLabel="Abbrechen"
        destructive
        onConfirm={confirmRemoveFile}
      />
    </div>
  );
}
